import React from "react";
import { FaFilePdf, FaFileExcel, FaSpinner } from "react-icons/fa";
import PropTypes from "prop-types";
import { exportDataIcon } from "../utils/svgs";
import "../styles/ExportButton.css";

/**
 * Global FMS Export Button component based on the Assets design.
 * Handles both PDF and Excel export buttons with standard active/disabled states.
 */
export const ExportButton = ({
  type = "pdf", // 'pdf' | 'excel' | 'custom'
  label,
  loading = false,
  loadingText = "Downloading...",
  disabled = false,
  hasActiveFilters,
  onClick,
  title,
  icon = exportDataIcon,
  className = "",
  style,
  ...rest
}) => {
  const isPdf = String(type || "").toLowerCase() === "pdf";
  const defaultLabel = isPdf ? "PDF Download" : "Excel Download";
  const buttonText = loading ? loadingText : label || defaultLabel;

  const isDisabled =
    Boolean(disabled) ||
    (hasActiveFilters !== undefined && !hasActiveFilters) ||
    Boolean(loading);

  const resolvedTitle =
    title !== undefined
      ? title
      : isDisabled && !loading
      ? "Select a search term or filter first"
      : undefined;

  return (
    <button
      type="button"
      className={`export_btn fms_export_btn ${className}`.trim()}
      onClick={onClick}
      disabled={isDisabled}
      aria-busy={loading}
      title={resolvedTitle}
      style={style}
      {...rest}
    >
      {icon} {buttonText}
    </button>
  );
};

ExportButton.propTypes = {
  type: PropTypes.oneOf(["pdf", "excel", "custom"]),
  label: PropTypes.node,
  loading: PropTypes.bool,
  loadingText: PropTypes.string,
  disabled: PropTypes.bool,
  hasActiveFilters: PropTypes.bool,
  onClick: PropTypes.func,
  title: PropTypes.string,
  icon: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.object,
};

/**
 * Renders both PDF and Excel export buttons together with standard layout and styling.
 */
export const ExportButtons = ({
  onExportPdf,
  pdfLoading = false,
  onExportExcel,
  excelLoading = false,
  disabled = false,
  hasActiveFilters,
  exportLoading = false,
  showPdf = true,
  showExcel = true,
  className = "",
  style,
}) => {
  return (
    <div className={`fms_export_buttons_group ${className}`.trim()} style={style}>
      {showPdf && (
        <ExportButton
          type="pdf"
          onClick={onExportPdf}
          loading={pdfLoading}
          disabled={disabled || exportLoading}
          hasActiveFilters={hasActiveFilters}
        />
      )}
      {showExcel && (
        <ExportButton
          type="excel"
          onClick={onExportExcel}
          loading={excelLoading}
          disabled={disabled || exportLoading}
          hasActiveFilters={hasActiveFilters}
        />
      )}
    </div>
  );
};

ExportButtons.propTypes = {
  onExportPdf: PropTypes.func,
  pdfLoading: PropTypes.bool,
  onExportExcel: PropTypes.func,
  excelLoading: PropTypes.bool,
  disabled: PropTypes.bool,
  hasActiveFilters: PropTypes.bool,
  exportLoading: PropTypes.bool,
  showPdf: PropTypes.bool,
  showExcel: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
};


/**
 * Compact Icon-only Export Button for tight spaces (modals, cards, inline rows).
 * Standardized for PDF and Excel with tooltip and loading spinner.
 */
export const ExportIconButton = ({
  type = "pdf", // 'pdf' | 'excel' | 'custom'
  loading = false,
  disabled = false,
  onClick,
  title,
  icon,
  className = "",
  style,
  ...rest
}) => {
  const isPdf = String(type || "").toLowerCase() === "pdf";
  const defaultTitle = isPdf ? "Export as PDF" : "Export as Excel";
  const loadingTitle = isPdf ? "Downloading PDF..." : "Downloading Excel...";

  const resolvedIcon = icon || (isPdf ? <FaFilePdf /> : <FaFileExcel />);

  return (
    <button
      type="button"
      className={`fms_export_icon_btn ${isPdf ? "pdf_icon_btn" : "excel_icon_btn"} ${className}`.trim()}
      onClick={onClick}
      disabled={disabled || loading}
      aria-busy={loading}
      title={loading ? loadingTitle : title || defaultTitle}
      style={style}
      {...rest}
    >
      {loading ? <FaSpinner className="fms_spin" /> : resolvedIcon}
    </button>
  );
};

ExportIconButton.propTypes = {
  type: PropTypes.oneOf(["pdf", "excel", "custom"]),
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func,
  title: PropTypes.string,
  icon: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default ExportButton;
